import { SelectionHeader } from '@/components/bulkQueue/cells/SelectionHeader';
import {
    useBulkTentativeDateContext,
    useDateRecommendations,
    useIsEntitySelected,
    useSelectedDate,
} from '@/components/bulkQueue/tables/tentativeDate/BulkTentativeDateContext';
import { CellProps, HeaderProps } from '@/components/bulkQueue/types';
import { DateDecisionSupport } from '@/components/decisionSupport/DateDecisionSupport';
import { DateRecommendationFragment } from '@/components/decisionSupport/queries/dateRecommendations.gql.types';
import { useAxleRecommended } from '@/components/decisionSupport/utils/decisionSupport.utils';
import { CaptionLabel } from '@/components/labels/CaptionLabel';
import { NoneLabel } from '@/components/labels/NoneLabel';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { GraphInputHelpers } from '@/fetching/graphql/GraphInputHelpers';
import { Checkbox } from '@mui/material';
import { FC, useCallback, useMemo } from 'react';

type DateRecommendationsCellProps = {
    isLoading: boolean;
    visitId: string;
    recommendations: DateRecommendationFragment[];
    selectedDate: string | null;
    setSelectedDate(visitId: string, date: string | null): void;
    disabled?: boolean;
};

export const DateRecommendationsCell: FC<DateRecommendationsCellProps> = (props) => {
    const { isLoading, setSelectedDate, visitId, selectedDate, recommendations, disabled } = props;

    const selectedDateRecommendation = useMemo(
        () =>
            recommendations.find(
                (recommendation) => GraphInputHelpers.date(recommendation.date) === selectedDate,
            ) ?? null,
        [recommendations, selectedDate],
    );

    const topRecommendation = useAxleRecommended(recommendations);

    const isSelectionDifferentFromTop =
        selectedDateRecommendation != null &&
        topRecommendation != null &&
        GraphInputHelpers.date(selectedDateRecommendation.date) !==
            GraphInputHelpers.date(topRecommendation.date);

    const onSelectedDate = useCallback(
        (recommendation: DateRecommendationFragment | null) => {
            setSelectedDate(visitId, GraphInputHelpers.date(recommendation?.date ?? null));
        },
        [setSelectedDate, visitId],
    );

    return (
        <VerticalStack>
            <CaptionLabel fontSize={'0.8em'}>
                {isSelectionDifferentFromTop ? 'Selected' : 'Recommended'}
            </CaptionLabel>
            <HorizontalStack spacing={1} sx={{ justifyContent: 'space-between' }}>
                {selectedDateRecommendation ? (
                    <>
                        {selectedDateRecommendation.date.toDateString()}
                        <DateDecisionSupport
                            selectedDateRecommendation={selectedDateRecommendation}
                            setSelectedDate={onSelectedDate}
                            recommendations={recommendations}
                            disabled={disabled}
                        />
                    </>
                ) : (
                    <NoneLabel text={isLoading ? 'Loading...' : 'None'} fontSize={'14px'} />
                )}
            </HorizontalStack>
        </VerticalStack>
    );
};

// Legacy prop-based version (used by UnifiedVisitsWorkflowTable, OrderVisitsWorkflowTable)
type LegacyDateRecommendationsSelectionCellProps = {
    entity: {
        id: string;
    };
    selectedDate: string | null;
    isLoading: boolean;
    isSelected: boolean;
    recommendations: DateRecommendationFragment[];
    isInFlight: boolean;
    setSelectedDate(visitId: string, date: string | null): void;
    setIsSelected(id: string, isSelected: boolean): void;
};

export const LegacyDateRecommendationsSelectionCell: FC<
    LegacyDateRecommendationsSelectionCellProps
> = (props) => {
    const {
        entity: { id },
        recommendations,
        isInFlight,
        isLoading,
        setIsSelected,
        isSelected,
        setSelectedDate,
        selectedDate,
    } = props;
    const hasRecommendations = recommendations.length > 0;

    return (
        <HorizontalStack
            justifyContent='space-between'
            onClick={(event) => {
                event.stopPropagation();
            }}
        >
            <VerticalStack>
                <DateRecommendationsCell
                    recommendations={recommendations}
                    setSelectedDate={setSelectedDate}
                    selectedDate={selectedDate}
                    visitId={id}
                    isLoading={isLoading}
                    disabled={isInFlight}
                />
            </VerticalStack>
            {hasRecommendations && (
                <Checkbox
                    size='small'
                    disabled={isLoading || isInFlight}
                    checked={isSelected}
                    onChange={(event) => {
                        event.stopPropagation();
                        setIsSelected(id, !isSelected);
                    }}
                />
            )}
        </HorizontalStack>
    );
};

// New context-based version (used by BulkTentativeDateAssignmentTable)
export const DateSelectionCell: FC<CellProps> = (props) => {
    const {
        entity: { id },
    } = props;

    const [recommendations, isLoading] = useDateRecommendations(id);
    const [selectedDate, setSelectedDate] = useSelectedDate(id);
    const [isSelected, setIsSelected] = useIsEntitySelected(id);
    const { state } = useBulkTentativeDateContext();
    const hasRecommendations = recommendations.length > 0;

    const setDate = useCallback(
        (_visitId: string, date: string | null) => {
            setSelectedDate(date);
        },
        [setSelectedDate],
    );

    return (
        <HorizontalStack
            justifyContent='space-between'
            onClick={(event) => {
                event.stopPropagation();
            }}
        >
            <VerticalStack>
                <DateRecommendationsCell
                    recommendations={recommendations}
                    setSelectedDate={setDate}
                    selectedDate={selectedDate}
                    visitId={id}
                    isLoading={isLoading}
                    disabled={state === 'submitting'}
                />
            </VerticalStack>
            {hasRecommendations && (
                <Checkbox
                    size='small'
                    disabled={isLoading || state === 'submitting'}
                    checked={isSelected}
                    onChange={(event) => {
                        event.stopPropagation();
                        setIsSelected(!isSelected);
                    }}
                />
            )}
        </HorizontalStack>
    );
};

export const DateSelectionHeader: FC<HeaderProps> = (props) => {
    const { entityCount, summary, state, selectedCount, submit, selectableCount, selectAll } =
        useBulkTentativeDateContext();
    return (
        <SelectionHeader
            {...props}
            hasItems={entityCount > 0}
            isLoading={state === 'loading'}
            isSubmitting={state === 'submitting'}
            isDisabled={state !== 'idle' || selectedCount === 0}
            numSelected={selectedCount}
            summaryString={summary}
            onApply={submit}
            selectableCount={selectableCount}
            onSelectAll={selectAll}
        />
    );
};
